import { useWorkspace } from '@/features/workspaces/hooks';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import {
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  useGetWorkspaceConnectorsQuery,
} from '@/services/endpoints/workspace-connectors';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CREATE_DATA_SOURCE_SOURCE_CONNECTOR,
  D365_BC_SYNC_LOGICAL_KEY,
  filterSourceConnectorWizardFlows,
  SOURCE_CONNECTOR_WIZARD_DIRECTION,
} from './constants';
import { SourceConnectorWizardData, SourceConnectorWizardRouteState } from './types';

type BootstrapReady = {
  isSeeded: true;
  isLoading: false;
  error: false;
  fromCatalogManage: boolean;
  wizardData: Partial<SourceConnectorWizardData>;
  initialStepId: CREATE_DATA_SOURCE_SOURCE_CONNECTOR.CONNECTOR;
};

type BootstrapError = {
  isSeeded: true;
  isLoading: false;
  error: true;
};

type BootstrapLoading = {
  isSeeded: true;
  isLoading: true;
  error?: undefined;
};

type BootstrapNotSeeded = {
  isSeeded: false;
  isLoading: false;
};

export type SourceConnectorWizardBootstrapResult = BootstrapNotSeeded | BootstrapLoading | BootstrapError | BootstrapReady;

export const isSourceConnectorBootstrapReady = (bootstrap: SourceConnectorWizardBootstrapResult): bootstrap is BootstrapReady =>
  bootstrap.isSeeded && !bootstrap.isLoading && bootstrap.error === false;

export const isSourceConnectorBootstrapError = (bootstrap: SourceConnectorWizardBootstrapResult): bootstrap is BootstrapError =>
  bootstrap.isSeeded && !bootstrap.isLoading && bootstrap.error === true;

export const useSourceConnectorWizardBootstrap = (): SourceConnectorWizardBootstrapResult => {
  const location = useLocation<SourceConnectorWizardRouteState | undefined>();
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();

  const connectorId = location.state?.connectorId;

  const fromCatalogManage = location.state?.fromCatalogManage ?? false;
  const isSeeded = Boolean(connectorId);

  const { data: connectors = [], isFetching: isFetchingConnectors } = useGetWorkspaceConnectorsQuery(
    {
      workspaceId,
      lang,
      queryParams: { direction: SOURCE_CONNECTOR_WIZARD_DIRECTION },
    },
    { skip: !isSeeded }
  );

  const { data: instances = [], isFetching: isFetchingInstances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !isSeeded });

  const connector = useMemo(
    () => connectors.find((c) => c.id === connectorId) as CustomConnectorDto | undefined,
    [connectorId, connectors]
  );

  const connectorInstance = useMemo(() => instances.find((instance) => instance.connectorId === connectorId), [connectorId, instances]);

  const { data: flows = [], isFetching: isFetchingFlows } = useGetConnectorFlowsQuery(
    {
      workspaceId,
      lang,
      queryParams: {
        direction: SOURCE_CONNECTOR_WIZARD_DIRECTION,
        connectorInstanceId: connectorInstance?.id,
      },
    },
    { skip: !isSeeded || !connectorInstance?.id }
  );

  return useMemo((): SourceConnectorWizardBootstrapResult => {
    if (!isSeeded) {
      return { isSeeded: false, isLoading: false };
    }

    const isLoading = isFetchingConnectors || isFetchingInstances || isFetchingFlows;
    if (isLoading) {
      return { isSeeded: true, isLoading: true };
    }

    const wizardFlows = filterSourceConnectorWizardFlows(flows);
    const defaultFlow = wizardFlows.find((flow) => flow.logicalKey === D365_BC_SYNC_LOGICAL_KEY) ?? wizardFlows[0];

    if (!connector || !connectorInstance || !defaultFlow) {
      return { isSeeded: true, isLoading: false, error: true };
    }

    return {
      isSeeded: true,
      isLoading: false,
      error: false,
      fromCatalogManage,
      initialStepId: CREATE_DATA_SOURCE_SOURCE_CONNECTOR.CONNECTOR,
      wizardData: {
        name: '',
        connector,
        connectorInstance,
        flow: defaultFlow as ConnectorFlowDTO,
      },
    };
  }, [connector, connectorInstance, flows, fromCatalogManage, isFetchingConnectors, isFetchingFlows, isFetchingInstances, isSeeded]);
};
